import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { handleError } from "../Library/Utils";

type CallOptions = {
  // Translation key toasted on success, so call sites don't each repeat
  // toast.success(t("Translation.xxx")) as the first line of onSuccess.
  successMessage?: string;
  onError?: (err?: any) => void;
};

export const useApiCall = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const call = async (
    apiCall: () => Promise<any>,
    onSuccess?: (data: any) => void,
    // A bare function keeps the original call(apiCall, onSuccess, onError) shape working
    onErrorOrOptions?: ((err?: any) => void) | CallOptions
  ) => {
    const { successMessage, onError }: CallOptions =
      typeof onErrorOrOptions === "function"
        ? { onError: onErrorOrOptions }
        : onErrorOrOptions ?? {};
    try {
      const res = await apiCall();
      // It should always return a response object with data, status and statusText, if not - check axios methods
      const { data, status, statusText } = res;
      switch (status) {
        case 200:
        case 201:
        case 204:
          if (successMessage) toast.success(t(successMessage));
          onSuccess?.(data);
          break;
        case 400:
        case 403:
        case 404:
        case 500:
          if (onError) onError(data);
          else handleError(data, t);
          break;
        default:
          if (statusText) toast.error(statusText);
          if (onError) onError(data);
          break;
      }
    } catch (err) {
      console.error(err);
      toast.error(t("Translation.error_something_went_wrong"));
      if (onError) onError(err);
    }
  };

  const recursivePaginatedCall = async (
    apiCall: (page: number) => Promise<any>,
    page = 1,
    prevResponse = []
  ): Promise<any> => {
    const response: any = [...prevResponse];
    const res = await apiCall(page);
    response.push(res.data.results);
    if (res.data.next) {
      return recursivePaginatedCall(apiCall, page + 1, response);
    } else {
      return {
        ...res,
        data: {
          ...res.data,
          results: response.flat(),
        },
      };
    }
  };

  return { call, recursivePaginatedCall };
};
